import { useState } from "react";
import { X } from "lucide-react";
import { fmtINR } from "@/lib/api";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function PositionCard({ pos }) {
  const [exiting, setExiting] = useState(false);
  const positive = pos.pnl >= 0;
  const color = positive ? "#00E676" : "#FF3B30";

  const doExit = async () => {
    setExiting(true);
    try {
      await api.exitPosition(pos.symbol);
      toast.success(`Exited ${pos.symbol}`, { description: `MTM ${fmtINR(pos.pnl)}` });
    } catch (e) {
      toast.error("Exit failed");
      setExiting(false);
    }
  };

  // progress between stop and target
  const range = pos.target_price - pos.stop_price;
  const at = Math.max(0, Math.min(100, ((pos.ltp - pos.stop_price) / range) * 100));

  return (
    <div className="bg-surface border border-edge rounded-sm p-3" data-testid={`position-card-${pos.symbol}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-bold text-sm tracking-tight flex items-center gap-1.5">
            {pos.symbol}
            {pos.live && (
              <span className="text-[8px] px-1 py-0.5 rounded-sm bg-neg/20 text-neg font-bold tracking-wider" data-testid={`live-badge-${pos.symbol}`}>
                LIVE{pos.state ? ` · ${pos.state.replace("_", " ")}` : ""}
              </span>
            )}
          </div>
          <div className="mono text-[11px] text-muted2 mt-0.5">
            {pos.qty} × {fmtINR(pos.entry_price, 2)}
          </div>
        </div>
        <div className="text-right">
          <div className="mono text-base font-bold" style={{ color }} data-testid={`position-pnl-${pos.symbol}`}>
            {fmtINR(pos.pnl)}
          </div>
          <div className="mono text-[11px]" style={{ color }}>
            {pos.pnl_pct >= 0 ? "+" : ""}{pos.pnl_pct.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* stop <-> target bar */}
      <div className="mt-2.5 relative h-1.5 w-full rounded-full overflow-hidden bg-edge">
        <div className="absolute left-0 top-0 h-full" style={{ width: "100%", background: "linear-gradient(90deg,#FF3B30 0%,#3a3f4a 45%,#00E676 100%)", opacity: 0.35 }} />
        <div className="absolute top-1/2 -translate-y-1/2 h-3 w-1 bg-white rounded-full" style={{ left: `calc(${at}% - 2px)` }} />
      </div>
      <div className="flex justify-between mono text-[10px] text-muted2 mt-1">
        <span>SL {fmtINR(pos.stop_price, 1)}</span>
        <span className="text-white">LTP {fmtINR(pos.ltp, 2)}</span>
        <span>TGT {fmtINR(pos.target_price, 1)}</span>
      </div>

      <button
        onClick={doExit}
        disabled={exiting}
        className="mt-2.5 w-full flex items-center justify-center gap-1 py-2 rounded-sm bg-neg/10 border border-neg/30 text-neg text-xs font-bold tracking-wider active:bg-neg/20 transition-colors duration-150 disabled:opacity-40"
        data-testid={`exit-btn-${pos.symbol}`}
      >
        <X size={13} strokeWidth={3} /> {exiting ? "EXITING…" : "EXIT NOW"}
      </button>
    </div>
  );
}
